import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Paperclip, FileText, MapPin } from "lucide-react";
import { toast } from "sonner";
import { kwd, mapLink, locText } from "@/lib/util";
import { openFile, fileUrl, errMsg } from "@/lib/api";
import { tr as t } from "@/lib/i18n";

export const Logo = ({ light = false, small = false }) => (
  <div className="flex items-center gap-3" data-testid="company-logo">
    <img src="/img/logo.png" alt="شعار المحيط الأطلسي" className={`${small ? "w-11 h-11" : "w-14 h-14"} rounded-full bg-white p-0.5 object-contain shrink-0 shadow`} />
    <div className="leading-tight">
      <div className={`font-head font-extrabold ${small ? "text-base" : "text-lg"} ${light ? "text-white" : "text-brand"}`}>{t("المحيط الأطلسي")}</div>
      <div className={`text-xs ${light ? "text-white/70" : "text-slate-500"}`}>{t("لنقل البضائع والمعدات")}</div>
    </div>
  </div>
);

export const Money = ({ fils, className = "", testid }) => (
  <span className={`ltr font-bold ${className}`} data-testid={testid}>{kwd(fils)} <span className="text-[0.75em] font-medium">{t("د.ك")}</span></span>
);

const PILL = {
  blue: "bg-blue-50 text-blue-800 border-blue-200", red: "bg-red-50 text-red-700 border-red-200",
  green: "bg-green-50 text-green-800 border-green-200", orange: "bg-orange-50 text-orange-800 border-orange-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200", amber: "bg-amber-50 text-amber-800 border-amber-200",
};
export const Pill = ({ color = "slate", children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-3 py-0.5 text-sm font-bold whitespace-nowrap ${PILL[color]}`}>{children}</span>
);
export const payColor = (s) => ({ unpaid: "red", partial: "orange", paid: "green" }[s] || "slate");
export const tripColor = (s) => ({ created: "slate", in_transit: "blue", delivered: "green" }[s] || "slate");
export const docColor = (s) => ({ valid: "green", expiring: "orange", expired: "red" }[s] || "slate");

export const Loader = () => (
  <div className="flex justify-center py-16" data-testid="loading"><Loader2 className="w-9 h-9 animate-spin text-blue-700" /></div>
);

export const Empty = ({ icon: Icon = FileText, text }) => (
  <div className="text-center py-14 text-slate-500" data-testid="empty-state">
    <Icon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
    <div className="text-lg">{text}</div>
  </div>
);

export const BackBar = ({ title, to }) => {
  const nav = useNavigate();
  return (
    <div className="bg-brand text-white safe-top sticky top-0 z-30">
      <div className="flex items-center gap-3 px-4 h-16">
        <button data-testid="back-button" onClick={() => (to ? nav(to) : nav(-1))} className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center active:scale-95">
          <ArrowRight className="w-6 h-6 ltr:rotate-180" />
        </button>
        <h1 className="text-xl font-extrabold truncate">{title}</h1>
      </div>
    </div>
  );
};

export const Field = ({ label, hint, children, required }) => (
  <label className="block">
    <span className="block mb-1.5 font-bold text-slate-800">{label}{required && <span className="text-red-600"> *</span>}</span>
    {children}
    {hint && <span className="block mt-1 text-sm text-slate-500">{hint}</span>}
  </label>
);

export const Row = ({ label, children, testid }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 border-b border-slate-100 last:border-0">
    <span className="text-slate-500 shrink-0">{label}</span>
    <span className="font-bold text-slate-900 text-end break-words min-w-0" data-testid={testid}>{children ?? "—"}</span>
  </div>
);

export function useOpenFile() {
  return async (id) => {
    try { await openFile(id); } catch (e) { toast.error(errMsg(e)); }
  };
}

export const FileButton = ({ fileId, label, testid = "open-file-btn", className = "btn btn-ghost btn-md" }) => {
  const open = useOpenFile();
  if (!fileId) return null;
  return <button type="button" data-testid={testid} className={className} onClick={() => open(fileId)}><Paperclip className="w-5 h-5" />{label || t("فتح المرفق")}</button>;
};

export const Thumb = ({ fileId, testid }) => {
  const [src, setSrc] = useState(null);
  const open = useOpenFile();
  useEffect(() => {
    let on = true;
    fileUrl(fileId).then((u) => on && setSrc(u)).catch(() => {});
    return () => { on = false; };
  }, [fileId]);
  return (
    <button type="button" data-testid={testid} onClick={() => open(fileId)} className="aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
      {src ? <img src={src} alt="صورة" className="w-full h-full object-cover" /> : <Loader2 className="w-6 h-6 m-auto animate-spin text-slate-400" />}
    </button>
  );
};

export const LocLink = ({ loc }) => {
  const href = mapLink(loc);
  if (!href) return <span className="text-slate-500 text-sm">{locText(loc)}</span>;
  return <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 underline text-sm ltr"><MapPin className="w-4 h-4" />{locText(loc)}</a>;
};

export const vehNo = (adm, plate) => [adm, plate].filter(Boolean).join("/") || "—";

export const VehNo = ({ adm, plate, testid }) => {
  if (!adm && !plate) return <span>—</span>;
  return (
    <span className="inline-block rounded-md border border-slate-300 bg-white text-slate-800 px-2 text-sm font-bold whitespace-nowrap align-middle ltr" data-testid={testid}>
      {vehNo(adm, plate)}
    </span>
  );
};

export const Section = ({ title, children, action }) => (
  <div className="card p-5">
    <div className="flex items-center justify-between mb-3 gap-2">
      <h3 className="text-lg font-extrabold">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);
